import { deleteTherapeuticBookProduct, listTherapeuticBookProducts } from "../../../services/api";
import { useEffect, useState } from "react";
import { FieldLabel } from "../AdminCommon";
import { therapeuticClassBadgeStyle, therapeuticEmptyStyle, therapeuticRowStyle, therapeuticTdStyle } from "../styles/adminDashboardFormStyles";
import {
    errorStyle,
    fieldStyle,
    therapeuticCountBadgeStyle,
    therapeuticDangerButtonStyle,
    therapeuticFilterLabelStyle,
    therapeuticFiltersStyle,
    therapeuticHeaderStyle,
    therapeuticInputStyle,
    therapeuticPageStyle,
    therapeuticPanelStyle,
    therapeuticPrimaryButtonStyle,
    therapeuticTableStyle,
    therapeuticThStyle,
    therapeuticTitleStyle,
} from "../styles/adminDashboardStyles";
import { TherapeuticBookProductModal } from "./TherapeuticBookProductModal";
import { THERAPEUTIC_FILTER_CLASS_OPTIONS } from "./therapeuticRules";

export function TherapeuticBookTab() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [error, setError] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [searchFilter, setSearchFilter] = useState("");
    const [classFilter, setClassFilter] = useState("");

    const fetchProducts = async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await listTherapeuticBookProducts();
            setProducts(data || []);
        } catch (err) {
            setError(err.response?.data?.message || "Impossible de charger le livret thérapeutique.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchProducts(); }, []);

    const handleCreated = (product) => {
        setProducts(prev => [...prev, product].sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "fr")));
    };

    const handleDelete = async (product) => {
        if (!globalThis.confirm(`Retirer ${product.name} du livret thérapeutique ?`)) return;
        setDeletingId(product.id);
        try {
            await deleteTherapeuticBookProduct(product.id);
            setProducts(prev => prev.filter(item => item.id !== product.id));
        } catch (err) {
            setError(err.response?.data?.message || "Impossible de retirer ce produit.");
        } finally {
            setDeletingId(null);
        }
    };

    const normalizedSearchFilter = searchFilter.trim().toLowerCase();
    const filteredProducts = products.filter(product => {
        const matchesSearch = !normalizedSearchFilter
            || String(product.name || "").toLowerCase().includes(normalizedSearchFilter)
            || String(product.code || "").includes(normalizedSearchFilter)
            || String(product.medicamentReferenceCip13Codes || "").includes(normalizedSearchFilter);
        const matchesClass = !classFilter || product.productClass === classFilter;
        return matchesSearch && matchesClass;
    });
    const hasActiveFilters = Boolean(normalizedSearchFilter || classFilter);
    const productCountLabel = `${filteredProducts.length} produit${filteredProducts.length > 1 ? "s" : ""}`;

    return (
        <div style={therapeuticPageStyle}>
            <div style={therapeuticPanelStyle}>
                <div style={therapeuticHeaderStyle}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <h2 style={therapeuticTitleStyle}>Produits du livret</h2>
                        <span style={therapeuticCountBadgeStyle}>
                            {hasActiveFilters ? `${productCountLabel} affiché${filteredProducts.length > 1 ? "s" : ""}` : productCountLabel}
                        </span>
                    </div>
                    <button type="button" onClick={() => setShowModal(true)} style={therapeuticPrimaryButtonStyle}>
                        Ajouter des produits
                    </button>
                </div>

                <div style={therapeuticFiltersStyle}>
                    <div style={fieldStyle}>
                        <FieldLabel htmlFor="therapeutic-book-search" style={therapeuticFilterLabelStyle}>Recherche</FieldLabel>
                        <input
                            id="therapeutic-book-search"
                            value={searchFilter}
                            onChange={event => setSearchFilter(event.target.value)}
                            placeholder="Nom ou code 13"
                            style={therapeuticInputStyle}
                        />
                    </div>
                    <div style={fieldStyle}>
                        <FieldLabel htmlFor="therapeutic-book-class-filter" style={therapeuticFilterLabelStyle}>Classe</FieldLabel>
                        <select
                            id="therapeutic-book-class-filter"
                            value={classFilter}
                            onChange={event => setClassFilter(event.target.value)}
                            style={therapeuticInputStyle}
                        >
                            {THERAPEUTIC_FILTER_CLASS_OPTIONS.map(option => (
                                <option key={option.value || "all"} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {error && <p style={errorStyle}>{error}</p>}

                {loading ? (
                    <p style={therapeuticEmptyStyle}>Chargement...</p>
                ) : products.length === 0 ? (
                    <p style={therapeuticEmptyStyle}>Aucun produit.</p>
                ) : filteredProducts.length === 0 ? (
                    <p style={therapeuticEmptyStyle}>Aucun produit ne correspond aux filtres.</p>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table style={therapeuticTableStyle}>
                            <thead>
                                <tr>
                                    <th style={therapeuticThStyle}>Produit</th>
                                    <th style={therapeuticThStyle}>Classe</th>
                                    <th style={therapeuticThStyle}>Code</th>
                                    <th style={therapeuticThStyle}>Unité thérapeutique</th>
                                    <th style={therapeuticThStyle}>Qté/boîte</th>
                                    <th style={therapeuticThStyle}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredProducts.map(product => (
                                    <tr key={product.id} style={therapeuticRowStyle}>
                                        <td style={therapeuticTdStyle}>
                                            <div style={{ fontWeight: 800, color: "#122A26" }}>{product.name}</div>
                                        </td>
                                        <td style={therapeuticTdStyle}>
                                            <span style={therapeuticClassBadgeStyle(product.productClass)}>{product.productClassLabel}</span>
                                        </td>
                                        <td style={{ ...therapeuticTdStyle, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", color: "#0C3B3C", fontWeight: 800 }}>{product.code}</td>
                                        <td style={therapeuticTdStyle}>{product.therapeuticUnit || "-"}</td>
                                        <td style={{ ...therapeuticTdStyle, fontWeight: 800, color: "#122A26" }}>{product.boxQuantity || "-"}</td>
                                        <td style={{ ...therapeuticTdStyle, textAlign: "right" }}>
                                            <button
                                                type="button"
                                                onClick={() => handleDelete(product)}
                                                disabled={deletingId === product.id}
                                                style={therapeuticDangerButtonStyle}
                                            >
                                                {deletingId === product.id ? "Retrait..." : "Retirer"}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showModal && (
                <TherapeuticBookProductModal
                    onClose={() => setShowModal(false)}
                    onCreated={handleCreated}
                />
            )}
        </div>
    );
}
